import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { from, of } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, finalize, take } from 'rxjs/operators';
import { TOrderDetailsActionType } from './order-details.models';
import * as OrderDetailsActions from './order-details.actions';
import { OrderDetailsFacade } from './order-details.facade';

@Injectable({ providedIn: 'root' })
export class OrderDetailsEffects {
  constructor(
    private readonly actions$: Actions,
    private plani8DataService: Plani8DataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private orderDetailsFacade: OrderDetailsFacade,
  ) {}

  getPlanningCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.GetPlanningCard),
      switchMap((action: OrderDetailsActions.GetPlanningCard) => {
        this.orderDetailsFacade.setShowPlanningCardLoader(true);
        return this.plani8DataService.fetchOrderDetailsPlanningCard(action.payload, action.destination).pipe(
          finalize(() => {
            this.orderDetailsFacade.setShowPlanningCardLoader(false);
          }),
          map((res) => new OrderDetailsActions.GetPlanningCardSuccess(res)),
          catchError(() => {
            return of(new OrderDetailsActions.GetPlanningCardFailure());
          }),
        );
      }),
    ),
  );

  getLearningCurveCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.GetLearningCurveCard),
      switchMap((action: OrderDetailsActions.GetLearningCurveCard) => {
        this.orderDetailsFacade.setLeaningCurveLoader(true);
        return this.plani8DataService.fetchOrderDetailsLearningCurveCard(action.payload).pipe(
          finalize(() => {
            this.orderDetailsFacade.setLeaningCurveLoader(false);
          }),
          map((res) => new OrderDetailsActions.GetLearningCurveCardSuccess(res)),
          catchError(() => {
            return of(new OrderDetailsActions.GetLearningCurveCardFailure());
          }),
        );
      }),
    ),
  );

  getAllLearningCurves$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.GetAllLearningCurves),
      switchMap(() => {
        this.orderDetailsFacade.setShowAllLearningCurvesLoader(true);
        return this.plani8DataService.getAllLearningCurves().pipe(
          finalize(() => {
            this.orderDetailsFacade.setShowAllLearningCurvesLoader(false);
          }),
          map((res) => new OrderDetailsActions.GetAllLearningCurvesSuccess(res)),
          catchError(() => {
            return of(new OrderDetailsActions.GetAllLearningCurvesFailure());
          }),
        );
      }),
    ),
  );

  editLearningCurveOrderEfficiency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.EditLearningCurveOrderEfficiency),
      switchMap((action: OrderDetailsActions.EditLearningCurveOrderEfficiency) => {
        this.orderDetailsFacade.setLeaningCurveLoader(true);
        return this.plani8DataService.editOrderDetailsLearningCurveOrderEfficiency(action.editPayload).pipe(
          finalize(() => {
            this.orderDetailsFacade.setLeaningCurveLoader(false);
          }),
          map(() => new OrderDetailsActions.GetLearningCurveCard(action.fetchPayload)),
          catchError(() => {
            return of(new OrderDetailsActions.EditHeaderFailure());
          }),
        );
      }),
    ),
  );

  getDetailsLearningCurve$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.GetViewLearningCurveModal),
      switchMap((action: OrderDetailsActions.GetViewLearningCurveModal) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.viewOrderDetailsLearningCurve(action.learningCurveId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new OrderDetailsActions.GetViewLearningCurveModalSuccess(res)),
          catchError(() => {
            return of(new OrderDetailsActions.GetViewLearningCurveModalFailure());
          }),
        ),
      ),
    ),
  );

  getHeader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.GetHeader),
      switchMap((action: OrderDetailsActions.GetHeader) => {
        this.orderDetailsFacade.setHeaderLoader(true);
        return this.plani8DataService.fetchOrderDetailsHeader(action.payload).pipe(
          map((res) => new OrderDetailsActions.GetHeaderSuccess(res)),
          catchError(() => {
            return of(new OrderDetailsActions.GetHeaderFailure());
          }),
          finalize(() => this.orderDetailsFacade.setHeaderLoader(false)),
        );
      }),
    ),
  );

  editHeader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.EditHeader),
      switchMap((action: OrderDetailsActions.EditHeader) => {
        this.orderDetailsFacade.setHeaderLoader(true);
        return this.plani8DataService.editOrderDetailsHeader(action.editPayload).pipe(
          map(() => {
            new OrderDetailsActions.EditHeaderSuccess();
            return new OrderDetailsActions.GetHeader({ id: action.editPayload.otl_id });
          }),
          catchError(() => {
            return of(new OrderDetailsActions.EditHeaderFailure());
          }),
          finalize(() => this.orderDetailsFacade.setHeaderLoader(false)),
        );
      }),
    ),
  );

  editDefaultCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.EditDefaultCount),
      switchMap((action: OrderDetailsActions.EditDefaultCount) => {
        this.orderDetailsFacade.setHeaderLoader(true);
        return this.plani8DataService.editDefaultCountOrderDetails(action.editPayload).pipe(
          map(() => new OrderDetailsActions.GetHeader({ id: action.editPayload.otl_id })),
          catchError(() => {
            return of(new OrderDetailsActions.EditDefaultCountFailure());
          }),
          finalize(() => this.orderDetailsFacade.setHeaderLoader(false)),
        );
      }),
    ),
  );

  getPoDetailsCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.GetPoDetailsCard),
      switchMap((action: OrderDetailsActions.GetPoDetailsCard) => {
        this.orderDetailsFacade.setPoDetailsLoader(true);
        return this.plani8DataService.getOrderPoCardDetails(action.poDetailsReq).pipe(
          map((res) => new OrderDetailsActions.GetPoDetailsCardSuccess(res)),
          catchError(() => {
            return of(new OrderDetailsActions.GetPoDetailsCardFailure());
          }),
          finalize(() => {
            this.orderDetailsFacade.setPoDetailsLoader(false);
          }),
        );
      }),
    ),
  );

  fetchProcessMappingDetailsCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.FetchProcessMappingDetailsCard),
      switchMap((action: OrderDetailsActions.FetchProcessMappingDetailsCard) => {
        this.orderDetailsFacade.setProcessMappingDetailsLoader(true);
        return this.plani8DataService.getProcessMapByOtlId(action.otlId).pipe(
          map((res) => new OrderDetailsActions.FetchProcessMappingDetailsCardSuccess(res)),
          catchError(() => of(new OrderDetailsActions.FetchProcessMappingDetailsCardFailure())),
          finalize(() => this.orderDetailsFacade.setProcessMappingDetailsLoader(false)),
        );
      }),
    ),
  );

  updatePoDetailsOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.AddUpdatePoDetailsOrder),
      switchMap((action: OrderDetailsActions.AddUpdatePoDetailsOrder) => {
        this.orderDetailsFacade.setPoDetailsLoader(true);
        return this.plani8DataService.updatePoCardOrder(action.updatePoDetailsOrderReq).pipe(
          map(() => {
            const otlID = (action.updatePoDetailsOrderReq?.['otl_id'] as string) || '';
            return new OrderDetailsActions.GetPoDetailsCard({ id: otlID });
          }),
          catchError(() => {
            return of(new OrderDetailsActions.AddUpdatePoDetailsOrderFailure());
          }),
          finalize(() => {
            this.orderDetailsFacade.setPoDetailsLoader(false);
          }),
        );
      }),
    ),
  );

  deletePoDetailsOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.DeletePoDetailsOrder),
      switchMap((action: OrderDetailsActions.DeletePoDetailsOrder) => {
        this.orderDetailsFacade.setPoDetailsLoader(true);
        return this.plani8DataService.deletePoCardOrders(action.deletePoDetailsOrderReq).pipe(
          map(() => {
            const otlID = (action.deletePoDetailsOrderReq?.['otl_id'] as string) || '';
            return new OrderDetailsActions.GetPoDetailsCard({ id: otlID });
          }),
          catchError(() => {
            return of(new OrderDetailsActions.DeletePoDetailsOrderFailure());
          }),
          finalize(() => {
            this.orderDetailsFacade.setPoDetailsLoader(false);
          }),
        );
      }),
    ),
  );

  getPoColorSizeQtyInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.GetPoColorSizeQtyDetails),
      switchMap((action: OrderDetailsActions.GetPoColorSizeQtyDetails) =>
        this.plani8DataService.fetchPoColorSizQtyDetails(action.poColorSizeReq).pipe(
          map((res) => new OrderDetailsActions.GetPoColorSizeQtyDetailsSuccess(res)),
          catchError(() => {
            return of(new OrderDetailsActions.GetPoColorSizeQtyDetailsFailure());
          }),
        ),
      ),
    ),
  );

  updatePoColorQtyByRatio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.UpdatePoColorQtyByRatio),
      switchMap((action: OrderDetailsActions.UpdatePoColorQtyByRatio) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updatePoColorQtyByRatio(action.updateColorQtyByRatioReq).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            const orderId = action.updateColorQtyByRatioReq?.['otl_id'];
            if (orderId) {
              new OrderDetailsActions.UpdatePoColorQtyByRatioSuccess();
              return new OrderDetailsActions.GetPoDetailsCard({ id: orderId });
            } else {
              return new OrderDetailsActions.UpdatePoColorQtyByRatioSuccess();
            }
          }),
          catchError(() => {
            return of(new OrderDetailsActions.UpdatePoColorQtyByRatioFailure());
          }),
        ),
      ),
    ),
  );

  updatePoColorSizeQtyManually$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.UpdatePoColorSizeQtyManually),
      switchMap((action: OrderDetailsActions.UpdatePoColorSizeQtyManually) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updatePoColorSizeQtyManually(action.updatePoColorSizeQtyManualReq).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            const orderId = action.updatePoColorSizeQtyManualReq?.['otl_id'];
            if (orderId) {
              new OrderDetailsActions.UpdatePoColorSizeQtyManuallySuccess();
              return new OrderDetailsActions.GetPoDetailsCard({ id: orderId });
            } else {
              return new OrderDetailsActions.UpdatePoColorSizeQtyManuallySuccess();
            }
          }),
          catchError(() => {
            return of(new OrderDetailsActions.UpdatePoColorQtyByRatioFailure());
          }),
        ),
      ),
    ),
  );

  fetchAllocationCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.GetAllocationCardDetails),
      switchMap((action: OrderDetailsActions.GetAllocationCardDetails) => {
        this.orderDetailsFacade.setOrderAllocationLoader(true);
        return this.plani8DataService.fetchAllocationCardDetails(action.otlId).pipe(
          finalize(() => {
            this.orderDetailsFacade.setOrderAllocationLoader(false);
          }),
          map((res) => new OrderDetailsActions.GetAllocationCardDetailsSuccess(res)),
          catchError(() => {
            return of(new OrderDetailsActions.GetAllocationCardDetailsFailure());
          }),
        );
      }),
    ),
  );

  getOrderTimelineCardDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.GetOrderTimelineCardDetails),
      switchMap((action: OrderDetailsActions.GetOrderTimelineCardDetails) => {
        this.orderDetailsFacade.setOrderTimelineLoader(true);
        return this.plani8DataService.fetchOrderTimelineCardDetails(action.otlId).pipe(
          finalize(() => this.orderDetailsFacade.setOrderTimelineLoader(false)),
          map((res) => new OrderDetailsActions.GetOrderTimelineCardDetailsSuccess(res)),
          catchError(() => of(new OrderDetailsActions.GetOrderTimelineCardDetailsFailure())),
        );
      }),
    ),
  );

  getLinewisePlannedQtyCardDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.GetLinewisePlannedQtyCardDetails),
      switchMap((action: OrderDetailsActions.GetLinewisePlannedQtyCardDetails) => {
        this.orderDetailsFacade.setLinewisePlannedQtyLoader(true);
        return this.plani8DataService.fetchLinewisePlannedQtyCardDetails(action.otlId).pipe(
          finalize(() => this.orderDetailsFacade.setLinewisePlannedQtyLoader(false)),
          map((res) => new OrderDetailsActions.GetLinewisePlannedQtyCardDetailsSuccess(res)),
          catchError(() => of(new OrderDetailsActions.GetLinewisePlannedQtyCardDetailsFailure())),
        );
      }),
    ),
  );

  fetchObDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderDetailsActionType.GetOperationBreakdownDetails),
      switchMap((action: OrderDetailsActions.GetOperationBreakdownDetails) => {
        this.orderDetailsFacade.setOperationBreakdownDetailsLoader(true);
        return this.plani8DataService.fetchOperationBreakdownDetails(action.otlId).pipe(
          map((res) => new OrderDetailsActions.GetOperationBreakdownDetailsSuccess(res)),
          catchError(() => of(new OrderDetailsActions.GetOperationBreakdownDetailsFailure())),
          finalize(() => this.orderDetailsFacade.setOperationBreakdownDetailsLoader(false)),
        );
      }),
    ),
  );
}
